import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import axiosRetry from "axios-retry";
import { config } from "../../config";

interface Common {
  apiDomain?: string;
  fetch(options: AxiosRequestConfig): Promise<any>;
}

class CommonService implements Common {
  private axiosInstance: AxiosInstance;
  apiDomain?: string;

  constructor() {
    this.apiDomain = config.links.baseAPIURL;

    this.axiosInstance = axios.create({
      baseURL: this.apiDomain,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });

    // Add a response interceptor for error handling
    this.axiosInstance.interceptors.response.use(
      (response: any) => response,
      (error: any) => {
        console.error("error.response:", error.response, error);
        if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          if (error.response.status < 200 || error.response.status >= 400) {
            const errMsg = "Internal server error. Please contact support.";
            console.log(errMsg);
            return Promise.reject(
              error.response.data ? error.response.data : { error: errMsg }
            );
          }
        } else if (error.request) {
          // The request was made but no response was received
          const errMsg = "No response received from server. Please try again.";
          console.log(errMsg);
          return Promise.reject(
            error.response.data ? error.response.data : { error: errMsg }
          );
        } else {
          // Something happened in setting up the request that triggered an Error
          const errMsg = "An error occurred while setting up the request.";
          console.log(errMsg);
          return Promise.reject(
            error.response.data ? error.response.data : { error: errMsg }
          );
        }
        return Promise.reject(error);
      }
    );

    axiosRetry(this.axiosInstance, {
      retries: 3,
      retryDelay: axiosRetry.exponentialDelay,
      retryCondition: (error) => {
        return (
          axiosRetry.isNetworkOrIdempotentRequestError(error) ||
          error.response?.status === 429
        );
      },
    });

    this.fetch = this.fetch.bind(this);
    this.healthCheck = this.healthCheck.bind(this);
  }

  async fetch(options: AxiosRequestConfig) {
    const response = await this.axiosInstance({
      ...options,
    });
    return response.data;
  }

  async healthCheck() {
    try {
      const response = await this.axiosInstance.get("/healthz");
      return response.status;
    } catch (error) {
      return error;
    }
  }
}

export default CommonService;
